import React, { memo, useEffect, useState, useRef } from 'react';
import { Mention, MentionsInput } from 'react-mentions';
import { mentionTagsStyles } from 'constants/index';
import { useGetHashtagsQuery, useGetMentionUsersQuery } from 'services';
import useTranslate from 'hooks/useTranslate';

interface MentionsComponentProps {
	formik?: any;
	fieldName: string;
	contentValue?: string;
	textAreaId?: string;
}

const MentionsComponent: React.FC<MentionsComponentProps> = ({
	formik = {},
	fieldName,
	contentValue = '',
	textAreaId = '',
}) => {
	const { translate } = useTranslate();
	const searchString =
		'order=DESC&hasExpired=false&inActive=false&type=all&socialPostType=all&connectionType=connected';
	const { data: { docs: hashtagList = [] } = {} } = useGetHashtagsQuery(searchString);
	const { data: { docs: mentionUsers = [] } = {} } = useGetMentionUsersQuery(searchString);

	const [transformedHashtagList, setTransformedHashtagList] = useState<any>([]);
	const [transformedUserList, setTransformedUserList] = useState<any>([]);

	// Use refs to track if we've already processed the data
	const processedHashtagsRef = useRef<string>('');
	const processedUsersRef = useRef<string>('');

	useEffect(() => {
		if (contentValue && formik?.setFieldValue) {
			formik.setFieldValue(fieldName, contentValue);
		}
	}, [contentValue, fieldName]);

	useEffect(() => {
		const hashtagKey = JSON.stringify(
			hashtagList?.map((tag: any) => ({ id: tag.id, name: tag.name })),
		);
		if (hashtagKey !== processedHashtagsRef.current) {
			processedHashtagsRef.current = hashtagKey;
			if (hashtagList?.length > 0) {
				setTransformedHashtagList(
					hashtagList
						?.map((tag: any) => ({
							id: String(tag.id || ''),
							display: String(tag.name || ''),
						}))
						.filter((tag: any) => tag.display && tag.id),
				);
			} else {
				setTransformedHashtagList([]);
			}
		}
	}, [hashtagList]);

	useEffect(() => {
		const userKey = JSON.stringify(
			mentionUsers?.map((tag: any) => ({ id: tag?.id, name: tag?.name, userType: tag?.userType })),
		);
		if (userKey !== processedUsersRef.current) {
			processedUsersRef.current = userKey;
			if (mentionUsers?.length > 0) {
				setTransformedUserList(
					mentionUsers
						?.map((tag: any) => ({
							id: String(tag?.id || ''),
							display: String(tag?.name || ''),
							userType: tag?.userType,
						}))
						.filter((tag: any) => tag.display && tag.id),
				);
			} else {
				setTransformedUserList([]);
			}
		}
	}, [mentionUsers]);

	const handleCommentChange = (e: any) => {
		formik.setFieldValue(fieldName, e.target.value);
		const value = e.target.value;

		const hashtagFindRegex = /(^|\s)(#[a-z\d-_]+)/gi;
		const foundTags = value.match(hashtagFindRegex);

		if (foundTags) {
			const newTags = foundTags
				.map((tag: any) => String(tag).trim().substring(1))
				.filter((tag: any) => tag && !transformedHashtagList.some((ht: any) => ht.display === tag));
			if (newTags.length > 0) {
				const newHashtags = newTags.map((tag: any) => ({
					id: `new-${String(tag)}`,
					display: String(tag),
				}));
				setTransformedHashtagList((prevList: any) => [...prevList, ...newHashtags]);
			}
		}
	};

	// Memoize the displayTransform functions to prevent infinite re-renders
	const userDisplayTransform = React.useCallback(
		(_id: string, display: string) => `@${display}`,
		[],
	);
	const hashtagDisplayTransform = React.useCallback(
		(_id: string, display: string) => `#${display}`,
		[],
	);

	return (
		<MentionsInput
			value={formik.values?.[fieldName]}
			placeholder={translate('Social.addCommentUseTag')}
			onChange={handleCommentChange}
			style={mentionTagsStyles}
			className="create-new-post-content-inner"
			id={textAreaId}
		>
			<Mention
				trigger="@"
				data={transformedUserList}
				className="mentions__user"
				// onAdd={onAddUser}
				appendSpaceOnAdd={true}
				markup="@[__display__](__id__)"
				displayTransform={userDisplayTransform}
			/>
			<Mention
				trigger="#"
				className="mentions__tag"
				data={transformedHashtagList}
				// onAdd={onAddHasgtag}
				appendSpaceOnAdd={true}
				markup="#[__display__](__id__)"
				displayTransform={hashtagDisplayTransform}
			/>
		</MentionsInput>
	);
};

export default memo(MentionsComponent);
